import { useEffect } from "react";

/**
 * useDocumentMeta — sets document.title and meta description (+ optional OG /
 * Twitter tags + canonical link) for the current route. Restores previous
 * values on unmount so SPA route changes don't leak state.
 *
 * Pass a `path` starting with "/" and it will be joined to
 * https://jjargent.com to form absolute canonical / og:url values.
 */
export function useDocumentMeta({
  title,
  description,
  path,
  ogTitle,
  ogDescription,
  ogUrl,
  ogImage,
  noindex = false,
}) {
  useEffect(() => {
    const prevTitle = document.title;
    const SITE_URL = "https://jjargent.com";
    const url = ogUrl || (path ? `${SITE_URL}${path}` : undefined);

    const setMeta = (name, content, attr = "name") => {
      if (!content) return null;
      let tag = document.querySelector(`meta[${attr}="${name}"]`);
      let created = false;
      if (!tag) {
        tag = document.createElement("meta");
        tag.setAttribute(attr, name);
        document.head.appendChild(tag);
        created = true;
      }
      const prev = tag.getAttribute("content");
      tag.setAttribute("content", content);
      return { tag, prev, created, kind: "meta" };
    };

    const setCanonical = (href) => {
      if (!href) return null;
      let tag = document.querySelector('link[rel="canonical"]');
      let created = false;
      if (!tag) {
        tag = document.createElement("link");
        tag.setAttribute("rel", "canonical");
        document.head.appendChild(tag);
        created = true;
      }
      const prev = tag.getAttribute("href");
      tag.setAttribute("href", href);
      return { tag, prev, created, kind: "link" };
    };

    if (title) document.title = title;
    const restorers = [
      setMeta("description", description),
      setMeta("og:title", ogTitle || title, "property"),
      setMeta("og:description", ogDescription || description, "property"),
      setMeta("og:url", url, "property"),
      setMeta("og:image", ogImage, "property"),
      setMeta("twitter:title", ogTitle || title),
      setMeta("twitter:description", ogDescription || description),
      setMeta("twitter:image", ogImage),
      setCanonical(url),
      noindex ? setMeta("robots", "noindex, nofollow") : null,
    ].filter(Boolean);

    return () => {
      document.title = prevTitle;
      restorers.forEach(({ tag, prev, created, kind }) => {
        if (created) tag.remove();
        else if (prev !== null) {
          tag.setAttribute(kind === "link" ? "href" : "content", prev);
        }
      });
    };
  }, [title, description, path, ogTitle, ogDescription, ogUrl, ogImage, noindex]);
}
